import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ymd } from '@/lib/officialsApi';

export const dayState = (entries, date) => {
  const dated = entries.filter(e => e.date === date);
  if (dated.some(e => e.type === 'unavailable' && !e.start)) return 'unavailable';
  const av = dated.some(e => e.type === 'available');
  const partUn = dated.some(e => e.type === 'unavailable');
  if (av && !partUn) return 'available';
  if (av || partUn) return 'partial';
  const wd = (new Date(`${date}T00:00:00`).getDay() + 6) % 7;
  const weekly = entries.some(e => e.type === 'weekly' && Number(e.weekday) === wd
    && (!e.from_date || e.from_date <= date) && (!e.until_date || date <= e.until_date));
  return weekly ? 'weekly' : 'none';
};

const STYLE = {
  available: 'bg-emerald-500 border-emerald-500 text-white',
  unavailable: 'bg-red-500 border-red-500 text-white',
  partial: 'bg-amber-300 border-amber-300 text-amber-950',
  weekly: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  none: 'bg-white border-slate-200 text-slate-700',
};
const LEGEND = [['available', 'Available'], ['unavailable', 'Unavailable'], ['partial', 'Some hours'], ['weekly', 'Weekly hours']];

export const DateCalendar = ({ entries, onPick }) => {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const lead = (month.getDay() + 6) % 7;
  const cells = Array.from({ length: 42 }, (_, i) => new Date(month.getFullYear(), month.getMonth(), 1 - lead + i));
  const today = ymd(new Date());
  return (
    <div data-testid="date-calendar">
      <div className="flex items-center justify-between mb-2">
        <Button variant="ghost" size="sm" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="date-calendar-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <div className="font-black text-[#00247D] uppercase" style={{ fontFamily: 'Barlow Condensed' }} data-testid="date-calendar-month">
          {month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="date-calendar-next"><ChevronRight className="w-4 h-4" /></Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase text-slate-400 mb-1">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map(d => {
          const k = ymd(d);
          const past = k < today;
          const inMonth = d.getMonth() === month.getMonth();
          return (
            <button key={k} type="button" disabled={past} onClick={() => onPick(k)} data-testid={`avail-day-${k}`}
              className={`h-10 sm:h-12 rounded-lg border text-sm font-semibold transition-transform ${STYLE[dayState(entries, k)]} ${inMonth ? '' : 'opacity-40'} ${past ? 'opacity-25 cursor-not-allowed' : 'hover:scale-[1.06]'} ${k === today ? 'ring-2 ring-[#00247D] ring-offset-1' : ''}`}>
              {d.getDate()}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-slate-500">
        {LEGEND.map(([k, l]) => <span key={k} className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded border ${STYLE[k]}`} />{l}</span>)}
      </div>
    </div>
  );
};
